<template>
  <div v-if="loaded" class="re-form">

    <div class="re-section re-section--info">
      <div class="re-section__head">
        <div class="re-section__icon"><feather-icon icon="RefreshCcwIcon" size="20"/></div>
        <div class="re-section__titles">
          <div class="re-section__title">{{ msg('Channel Manager') }}</div>
          <div class="re-section__sub">{{ msg('Sync reservations from the channel manager') }}</div>
        </div>
      </div>
      <div class="re-section__body">
    <b-row class="mt-1 w-100">
      <b-col cols="3">
        <b-form-group label="Channel ID" label-for="realEstate-buy-price">
          <b-form-input id="realEstate-buy-price"  v-model="realEstate.channelId"
                        @change="onValueUpdated"/>
        </b-form-group>
      </b-col>


      <b-col cols="3">
        <b-form-group :label="msg('Channel manager reservations')" label-for="realEstate-sell-price">
          <b-button variant="primary" :disabled="channelLoading" @click="fetchChannelReservations">
            <b-spinner small v-if="channelLoading" class="mr-2"></b-spinner>
            {{ msg('Fetch reservations from channel manager') }}
          </b-button>
        </b-form-group>
      </b-col>
      <b-col cols="3">
        <b-form-group :label="msg('Crm contracts out of channel manager')" label-for="realEstate-sell-price">
          <b-badge variant="primary">
            {{ totalReservations != null ? totalReservations : "Not available at this moment" }}
          </b-badge>

        </b-form-group>
      </b-col>
    </b-row>

    <!-- Channel manager fetch report -->
    <b-row v-if="fetchReport != null" class="mt-1 w-100">
      <b-col cols="12">
        <b-card no-body class="fetch-report border">
          <div class="fetch-report__head d-flex align-items-center justify-content-between px-1 py-75">
            <div class="d-flex align-items-center flex-wrap">
              <feather-icon icon="DownloadCloudIcon" size="17" class="mr-50 text-primary"/>
              <strong>{{ msg('Channel fetch result') }}</strong>
              <b-badge variant="light-primary" class="ml-50">{{ fetchReport.channel }}</b-badge>
              <small class="text-muted ml-1">{{ fetchReportTime }}</small>
            </div>
            <feather-icon icon="XIcon" size="16" class="cursor-pointer" @click="fetchReport = null"/>
          </div>

          <div class="fetch-report__stats px-1 pb-75">
            <div class="fetch-report__stat">
              <span class="fetch-report__num text-primary">{{ fetchReport.totalOnChannel }}</span>
              <span class="fetch-report__lbl">{{ msg('Total on channel') }}</span>
            </div>
            <div class="fetch-report__stat">
              <span class="fetch-report__num text-success">{{ fetchReport.inserted }}</span>
              <span class="fetch-report__lbl">{{ msg('Inserted') }}</span>
            </div>
            <div class="fetch-report__stat">
              <span class="fetch-report__num text-info">{{ fetchReport.relinked }}</span>
              <span class="fetch-report__lbl">{{ msg('Relinked') }}</span>
            </div>
            <div class="fetch-report__stat">
              <span class="fetch-report__num text-secondary">{{ fetchReport.skippedExisting }}</span>
              <span class="fetch-report__lbl">{{ msg('Already in CRM') }}</span>
            </div>
            <div class="fetch-report__stat">
              <span class="fetch-report__num text-warning">{{ fetchReport.skippedStatus }}</span>
              <span class="fetch-report__lbl">{{ msg('Skipped by status') }}</span>
            </div>
            <div class="fetch-report__stat">
              <span class="fetch-report__num text-danger">{{ fetchReport.errors }}</span>
              <span class="fetch-report__lbl">{{ msg('Errors') }}</span>
            </div>
          </div>

          <b-table v-if="reportItems.length > 0" :items="reportItems" :fields="reportFields"
                   small responsive striped class="mb-0 fetch-report__table">
            <template #cell(action)="data">
              <b-badge :variant="actionVariant(data.value)">{{ actionLabel(data.value) }}</b-badge>
            </template>
            <template #cell(reason)="data">
              <span class="fetch-report__reason">{{ reasonText(data.item) }}</span>
            </template>
          </b-table>
          <div v-else class="text-center text-muted py-1">
            {{ msg('No reservations found on the channel manager') }}
          </div>
        </b-card>
      </b-col>
    </b-row>
      </div>
    </div>

    <div class="re-section re-section--primary">
      <div class="re-section__head">
        <div class="re-section__icon"><feather-icon icon="CalendarIcon" size="20"/></div>
        <div class="re-section__titles">
          <div class="re-section__title">{{ msg('Rental Style') }}</div>
          <div class="re-section__sub">{{ msg('Short-term BnB style and minimum-stay rules') }}</div>
        </div>
      </div>
      <div class="re-section__body">
    <b-row class="w-100">

      <b-col cols="3">
        <b-form-group :description="msg('BnB Style rent (default ab 1 Nacht)') ">
          <b-form-checkbox
              v-model="realEstate.bnbStyle"
              name="check-button" switch
              @change="onValueUpdatedBnb">
          </b-form-checkbox>
        </b-form-group>

      </b-col>

      <b-col cols="3">
        <b-form-group :description="msg('30 Nächte Minimum') ">
          <b-form-checkbox
              v-model="realEstate.bnbStyle30"
              name="check-button" switch
              @change="onValueUpdatedBnb30">
          </b-form-checkbox>
        </b-form-group>

      </b-col>
    </b-row>
      </div>
    </div>

    <div class="re-section re-section--success">
      <div class="re-section__head">
        <div class="re-section__icon"><feather-icon icon="ClockIcon" size="20"/></div>
        <div class="re-section__titles">
          <div class="re-section__title">{{ msg('Check-in & Access') }}</div>
          <div class="re-section__sub">{{ msg('Check-in / out times, key details and WiFi') }}</div>
        </div>
      </div>
      <div class="re-section__body">
    <b-row class="w-100">

      <b-col cols="12" md="6" lg="3">
        <b-form-group :label="msg('CheckIn time')" label-for="messageTemplate-company">
          <v-select
              :dir="isRTL ? 'rtl' : 'ltr'"
              :clearable="false"
              :options="timeOptions"
              v-model="realEstate.checkInTime"
              class="w-100"
              label="label"
              :reduce="(branch) => branch.value"
          ></v-select>
        </b-form-group>
      </b-col>

      <b-col cols="12" md="6" lg="3">
        <b-form-group :label="msg('CheckOut time')" label-for="messageTemplate-company">

          <v-select
              :dir="isRTL ? 'rtl' : 'ltr'"
              :clearable="false"
              :options="timeOptions"
              v-model="realEstate.checkOutTime"
              class="w-100"
              label="label"
              :reduce="(branch) => branch.value"
          ></v-select>
        </b-form-group>
      </b-col>
      <b-col cols="12" md="6" lg="3">
        <b-form-group :label="msg('Early CheckIn time')" label-for="messageTemplate-company">
          <v-select
              :dir="isRTL ? 'rtl' : 'ltr'"
              :clearable="false"
              :options="timeOptions"
              v-model="realEstate.earlyCheckInTime"
              class="w-100"
              label="label"
              :reduce="(branch) => branch.value"
          ></v-select>
        </b-form-group>
      </b-col>

      <b-col cols="12" md="6" lg="3">
        <b-form-group :label="msg('Late CheckOut time')" label-for="messageTemplate-company">

          <v-select
              :dir="isRTL ? 'rtl' : 'ltr'"
              :clearable="false"
              :options="timeOptions"
              v-model="realEstate.lateCheckOutTime"
              class="w-100"
              label="label"
              :reduce="(branch) => branch.value"
          ></v-select>
        </b-form-group>
      </b-col>
      <b-col cols="12">
        <b-form-group label="CheckIn Details" label-for="realEstate-buy-price">
          <b-textarea id="realEstate-buy-price" v-model="realEstate.realEstateTreasure.keyCode"
                      @change="onValueUpdated"/>
        </b-form-group>
      </b-col>
      <b-col cols="3">
        <b-form-group label="Wifi Username" label-for="realEstate-buy-price">
          <b-form-input id="realEstate-buy-price" v-model="realEstate.realEstateTreasure.wifiUsername"
                        @change="onValueUpdated"/>
        </b-form-group>
      </b-col>
      <b-col cols="3">
        <b-form-group label="Wifi Password" label-for="realEstate-buy-price">
          <b-form-input id="realEstate-buy-price" v-model="realEstate.realEstateTreasure.wifiPassword"
                        @change="onValueUpdated"/>
        </b-form-group>
      </b-col>
    </b-row>
      </div>
    </div>

    <div class="re-section re-section--gold">
      <div class="re-section__head">
        <div class="re-section__icon"><feather-icon icon="PercentIcon" size="20"/></div>
        <div class="re-section__titles">
          <div class="re-section__title">{{ msg('Pricing & Discounts') }}</div>
          <div class="re-section__sub">{{ msg('Payment terms, long-stay discounts and reviews') }}</div>
        </div>
      </div>
      <div class="re-section__body">
    <b-row class="w-100">

      <b-col cols="3">
        <b-form-group :label="msg('One payment') +' in %'" label-for="realEstate-buy-price">
          <b-form-input id="realEstate-buy-price" type="number" v-model="realEstate.realEstateTreasure.oneTimePayment"
                        @change="onValueUpdated"/>
        </b-form-group>
      </b-col>
      <b-col cols="3">
        <b-form-group :label="msg('Discount 3-6 months') +' in %'" label-for="realEstate-buy-price">
          <b-form-input id="realEstate-buy-price" type="number" v-model="realEstate.realEstateTreasure.discount3"
                        @change="onValueUpdated"/>
        </b-form-group>
      </b-col>

      <b-col cols="3">
        <b-form-group :label="msg('Discount 6-12 months') +' in %'" label-for="realEstate-buy-price">
          <b-form-input id="realEstate-buy-price" type="number" v-model="realEstate.realEstateTreasure.discount6"
                        @change="onValueUpdated"/>
        </b-form-group>
      </b-col>

      <b-col cols="3">
        <b-form-group :label="msg('Discount 12+ months') +' in %'" label-for="realEstate-buy-price">
          <b-form-input id="realEstate-buy-price" type="number" v-model="realEstate.realEstateTreasure.discount12"
                        @change="onValueUpdated"/>
        </b-form-group>
      </b-col>

      <b-col cols="12">
        <b-form-group :label="msg('Review Online')" label-for="realEstate-buy-price">
          <b-form-textarea v-model="realEstate.realEstateTreasure.review" @change="()=>onValueUpdated"
                           size="sm"></b-form-textarea>

        </b-form-group>
      </b-col>

    </b-row>
      </div>
    </div>

    <div class="re-section re-section--danger">
      <div class="re-section__head">
        <div class="re-section__icon"><feather-icon icon="ShieldIcon" size="20"/></div>
        <div class="re-section__titles">
          <div class="re-section__title">{{ msg('Cancellation Policy') }}</div>
          <div class="re-section__sub">{{ msg('Refund windows and cancellation fees') }}</div>
        </div>
      </div>
      <div class="re-section__body">
    <b-row>
      <b-col>
        <b-form>
          <b-form-checkbox-group v-model="selectedOptions">
            <b-form-checkbox value="threeDays">{{ msg('3 Days') }}</b-form-checkbox>
            <b-form-checkbox value="sevenDays">{{ msg('7 Days') }}</b-form-checkbox>
            <b-form-checkbox value="fourteenDays">{{ msg('14 Days') }}</b-form-checkbox>
            <!-- Always checked and no input needed for non-refundable -->
            <b-form-checkbox value="nonRefundable" disabled>{{ msg('Non - refundable') }}</b-form-checkbox>
          </b-form-checkbox-group>

          <b-row class="mt-3">
            <!-- Input for threeDays -->
            <b-col v-if="selectedOptions.includes('threeDays')" md="2">
              <b-form-group :label="msg('3 Days Fee Percentage')">
                <b-form-input
                    type="number"
                    v-model="realEstate.realEstateCancel.threeDaysFeePercent"
                    :placeholder="msg('Enter percentage')"
                ></b-form-input>
              </b-form-group>
            </b-col>

            <!-- Input for sevenDays -->
            <b-col v-if="selectedOptions.includes('sevenDays')" md="2">
              <b-form-group :label="msg('7 Days Fee Percentage')">
                <b-form-input
                    type="number"
                    v-model="realEstate.realEstateCancel.sevenDaysFeePercent"
                    :placeholder="msg('Enter percentage')"
                ></b-form-input>
              </b-form-group>
            </b-col>

            <!-- Input for fourteenDays -->
            <b-col v-if="selectedOptions.includes('fourteenDays')" md="2">
              <b-form-group :label="msg('14 Days Fee Percentage')">
                <b-form-input
                    type="number"
                    v-model="realEstate.realEstateCancel.fourteenDaysFeePercent"
                    :placeholder="msg('Enter percentage')"
                ></b-form-input>
              </b-form-group>
            </b-col>
          </b-row>
        </b-form>
      </b-col>
    </b-row>
      </div>
    </div>
  </div>
</template>

<script>
import {mapActions, mapGetters} from 'vuex'

export default {
  name: 'realEstateTreasureRent',
  components: {},
  props: {
    value: {
      type: Object,
      required: true,
    },
  },
  data: () => ({
    loaded: false,
    channelLoading: false,
    totalReservations: null,
    fetchReport: null,
    fetchReportTime: null,
    selectedOptions: ['nonRefundable'], // To hold selected checkboxes
    timeOptions:
        [
          {label: "00:00", value: "00:00"},
          {label: "00:30", value: "00:30"},
          {label: "01:00", value: "01:00"},
          {label: "01:30", value: "01:30"},
          {label: "02:00", value: "02:00"},
          {label: "02:30", value: "02:30"},
          {label: "03:00", value: "03:00"},
          {label: "03:30", value: "03:30"},
          {label: "04:00", value: "04:00"},
          {label: "04:30", value: "04:30"},
          {label: "05:00", value: "05:00"},
          {label: "05:30", value: "05:30"},
          {label: "06:00", value: "06:00"},
          {label: "06:30", value: "06:30"},
          {label: "07:00", value: "07:00"},
          {label: "07:30", value: "07:30"},
          {label: "08:00", value: "08:00"},
          {label: "08:30", value: "08:30"},
          {label: "09:00", value: "09:00"},
          {label: "09:30", value: "09:30"},
          {label: "10:00", value: "10:00"},
          {label: "10:30", value: "10:30"},
          {label: "11:00", value: "11:00"},
          {label: "11:30", value: "11:30"},
          {label: "12:00", value: "12:00"},
          {label: "12:30", value: "12:30"},
          {label: "13:00", value: "13:00"},
          {label: "13:30", value: "13:30"},
          {label: "14:00", value: "14:00"},
          {label: "14:30", value: "14:30"},
          {label: "15:00", value: "15:00"},
          {label: "15:30", value: "15:30"},
          {label: "16:00", value: "16:00"},
          {label: "16:30", value: "16:30"},
          {label: "17:00", value: "17:00"},
          {label: "17:30", value: "17:30"},
          {label: "18:00", value: "18:00"},
          {label: "18:30", value: "18:30"},
          {label: "19:00", value: "19:00"},
          {label: "19:30", value: "19:30"},
          {label: "20:00", value: "20:00"},
          {label: "20:30", value: "20:30"},
          {label: "21:00", value: "21:00"},
          {label: "21:30", value: "21:30"},
          {label: "22:00", value: "22:00"},
          {label: "22:30", value: "22:30"},
          {label: "23:00", value: "23:00"},
          {label: "23:30", value: "23:30"}
        ],


  }),
  watch: {
    // Watch for changes in selectedOptions and update corresponding booleans
    selectedOptions(newSelectedOptions) {
      // Set booleans to true if the corresponding option is selected, false otherwise
      this.realEstate.realEstateCancel.threeDays = newSelectedOptions.includes('threeDays');
      this.realEstate.realEstateCancel.sevenDays = newSelectedOptions.includes('sevenDays');
      this.realEstate.realEstateCancel.fourteenDays = newSelectedOptions.includes('fourteenDays');
    }
  },
  computed: {
    ...mapGetters('realEstate', ['getAllTypes']),
    ...mapGetters('data', ['getAllBranches']),
    ...mapGetters('data', ['getAllCountries', 'getAllLanguage']),
    allBranches() {
      return Object.values(this.getAllBranches)
    },
    allCountries() {
      return Object.values(this.getAllCountries)
    },
    realEstate: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit('input', val)
      }
    },
    reportFields() {
      return [
        {key: 'reservationId', label: this.msg('Reservation ID')},
        {key: 'guest', label: this.msg('Guest')},
        {key: 'arrival', label: this.msg('Arrival')},
        {key: 'departure', label: this.msg('Departure')},
        {key: 'channelStatus', label: this.msg('Channel status')},
        {key: 'action', label: this.msg('Result')},
        {key: 'reason', label: this.msg('Reason')},
      ]
    },
    reportItems() {
      if (this.fetchReport == null || this.fetchReport.items == null)
        return [];
      const order = {ERROR: 0, INSERTED: 1, RELINKED: 2, SKIPPED_EXISTING: 3, SKIPPED_STATUS: 4};
      return [...this.fetchReport.items].sort((a, b) => {
        const oa = order[a.action] !== undefined ? order[a.action] : 9;
        const ob = order[b.action] !== undefined ? order[b.action] : 9;
        return oa - ob;
      });
    },
  },
  created() {
    this.loaded = true
    this.fetchReservationCount()
    // Set default percentage values if they are null

  },
  methods: {
    ...mapActions('nuki', {_loadNukiList: 'getNukiList', _sendMail: 'sendMail'}),
    ...mapActions('realEstate', ['fetchChannelContracts', 'getRealEstateReservationCountById']),

    fetchReservationCount() {
      let $this = this;
      this.getRealEstateReservationCountById(this.realEstate.id)
          .then(response => {
            $this.totalReservations = response.totalReservations;

          });

    },
    fetchChannelReservations() {
      this.channelLoading = true; // Start loading
      this.fetchReport = null;
      this.fetchChannelContracts(this.realEstate.id)
          .then((ans) => {
            this.fetchReport = ans != null ? ans.report : null;
            this.fetchReportTime = new Date().toLocaleString();
            this.fetchReservationCount();
          })
          .catch(error => {
            // Handle error if needed
            console.error("An error occurred:", error);
          })
          .finally(() => {
            this.channelLoading = false; // Stop loading on either success or error
          });
    },
    actionVariant(action) {
      switch (action) {
        case 'INSERTED':
          return 'light-success';
        case 'RELINKED':
          return 'light-info';
        case 'SKIPPED_EXISTING':
          return 'light-secondary';
        case 'SKIPPED_STATUS':
          return 'light-warning';
        case 'ERROR':
          return 'light-danger';
        default:
          return 'light-secondary';
      }
    },
    actionLabel(action) {
      switch (action) {
        case 'INSERTED':
          return this.msg('Inserted');
        case 'RELINKED':
          return this.msg('Relinked');
        case 'SKIPPED_EXISTING':
          return this.msg('Already in CRM');
        case 'SKIPPED_STATUS':
          return this.msg('Skipped by status');
        case 'ERROR':
          return this.msg('Error');
        default:
          return action;
      }
    },
    reasonText(item) {
      switch (item.action) {
        case 'INSERTED':
          return this.msg('New contract was created');
        case 'RELINKED':
          return this.msg('Existing contract matched by dates and linked to the new reservation id');
        case 'SKIPPED_EXISTING':
          return this.msg('A contract with this reservation id already exists');
        case 'SKIPPED_STATUS':
          return this.msg('Ignored because of the channel status');
        case 'ERROR':
          return item.reason || this.msg('Error');
        default:
          return item.reason || '';
      }
    },
    onValueUpdated(args) {
      this.$emit('change', args)
    },
    onValueUpdatedBnb30(args) {
      if (this.realEstate.bnbStyle30)
        this.realEstate.bnbStyle = true
      this.$emit('change', args)
    },
    onValueUpdatedBnb(args) {
      if (!this.realEstate.bnbStyle)
        this.realEstate.bnbStyle30 = false
      this.$emit('change', args)
    },

  }
}
</script>

<style>
.realEstate-description .ql-editor {
  min-height: 200px;
}
</style>

<style lang="scss" scoped>
.fetch-report {
  &__stats {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
  }

  &__stat {
    flex: 1 1 110px;
    min-width: 110px;
    text-align: center;
    padding: 0.5rem 0.75rem;
    border: 1px solid rgba(34, 41, 47, 0.1);
    border-radius: 0.428rem;
  }

  &__num {
    display: block;
    font-size: 1.4rem;
    font-weight: 600;
    line-height: 1.2;
  }

  &__lbl {
    font-size: 0.78rem;
    opacity: 0.75;
  }

  &__reason {
    font-size: 0.85rem;
  }
}

.dark-layout .fetch-report__stat {
  border-color: rgba(255, 255, 255, 0.12);
}
</style>
